// Firebase v9+ modular SDK imports
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence, enableMultiTabIndexedDbPersistence } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missingFields = requiredFields.filter(field => !firebaseConfig[field]);
  
  if (missingFields.length > 0) {
    console.error('❌ Firebase configuration error!');
    console.error('Missing required fields:', missingFields);
    console.error('Available environment variables:', Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')));
    console.error('Current config:', {
      ...firebaseConfig,
      apiKey: firebaseConfig.apiKey ? firebaseConfig.apiKey.substring(0, 10) + '...' : 'MISSING'
    });
    throw new Error(`Firebase configuration incomplete. Missing: ${missingFields.join(', ')}`);
  }
  
  console.log('✅ Firebase configuration validated successfully');
  console.log('📍 Project:', firebaseConfig.projectId);
  console.log('🔑 API Key:', firebaseConfig.apiKey.substring(0, 10) + '...');
};

// Validate before initializing
validateFirebaseConfig();

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const rtdb = getDatabase(app);

// Debug: Verify Realtime Database initialization
console.log('Realtime Database initialized:', rtdb);

// Enable Firestore offline persistence
const enableFirestorePersistence = async () => {
  try {
    // Try to enable multi-tab IndexedDB persistence (recommended)
    await enableMultiTabIndexedDbPersistence(db);
    console.log('✅ Firestore multi-tab offline persistence enabled');
  } catch (err) {
    if (err.code === 'failed-precondition') {
      // Multiple tabs open, persistence can only be enabled in one tab at a time
      console.warn('⚠️ Firestore persistence failed: Multiple tabs open');
      try {
        // Fallback to single-tab persistence
        await enableIndexedDbPersistence(db);
        console.log('✅ Firestore single-tab offline persistence enabled');
      } catch (fallbackErr) {
        if (fallbackErr.code === 'unimplemented') {
          console.warn('⚠️ Firestore persistence not available in this browser');
        } else {
          console.error('❌ Firestore persistence failed:', fallbackErr);
        }
      }
    } else if (err.code === 'unimplemented') {
      // The current browser doesn't support persistence
      console.warn('⚠️ Firestore persistence not supported in this browser');
    } else {
      console.error('❌ Firestore persistence initialization failed:', err);
    }
  }
};

// Initialize persistence (non-blocking)
enableFirestorePersistence().catch(error => {
  console.error('Error initializing Firestore persistence:', error);
});

// Export the app instance for additional configuration if needed
export default app;
